"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { FiEdit2, FiSave, FiArrowLeft } from "react-icons/fi";
import AlertDialog from "./AlertDialog";

export default function UserProfile() {
  const [user, setUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { userId } = useParams();

  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
    showCopyButton: false,
  });

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://rentdoc-a0d223e1499e.herokuapp.com/api/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching user:", err);
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load user information. Please try again.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        showCopyButton: false,
      });
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditingUser({
      ...user,
      phoneNumber: user.phoneNumber || "",
    });
    setIsEditing(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditingUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleActive = () => {
    setEditingUser((prev) => ({ ...prev, active: !prev.active }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/${userId}`,
        editingUser,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser(response.data.user);
      setIsEditing(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "User profile updated successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to save changes. Please try again.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingUser(null);
  };

  const formatRole = (role) => {
    const map = {
      unassigned: "Unassigned",
      admin: "Admin",
      property_manager: "Property Manager",
    };
    return map[role] || role;
  };

  if (loading)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500 text-lg">Loading user profile...</div>
      </div>
    );
  if (!user)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500 text-lg">User not found.</div>
      </div>
    );

  const inputClass =
    "w-full bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3 px-4 text-sm text-gray-900";
  const labelClass =
    "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5";
  const valueClass = "text-sm font-medium text-gray-900";

  const activeValue = isEditing ? editingUser.active : user.active;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <AlertDialog
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          confirmText="OK"
          cancelText="Close"
          open={dialog.open}
          onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
          onConfirm={dialog.onConfirm}
          showCopyButton={dialog.showCopyButton}
        />

        {/* Page header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <button
              onClick={() => navigate("/users")}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-indigo-600 font-medium mb-2 transition-colors"
            >
              <FiArrowLeft className="w-4 h-4" />
              Back to User Management
            </button>
            <h1 className="text-3xl font-semibold text-gray-900">
              {user.displayName}
            </h1>
            <p className="text-gray-500 mt-1">User profile and settings</p>
          </div>
          <div className="flex gap-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-2xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-2xl shadow-lg transition"
                >
                  <FiSave className="w-4 h-4" />
                  Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={handleEdit}
                className="flex items-center gap-2 px-6 py-2.5 bg-white border border-gray-200 hover:border-indigo-500 hover:text-indigo-600 text-gray-700 text-sm font-semibold rounded-2xl transition shadow-sm"
              >
                <FiEdit2 className="w-4 h-4" />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main info card */}
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
            <h2 className="text-base font-semibold text-gray-900 mb-6">
              Profile Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Display Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="displayName"
                    value={editingUser.displayName || ""}
                    onChange={handleChange}
                    className={inputClass}
                  />
                ) : (
                  <p className={valueClass}>{user.displayName}</p>
                )}
              </div>

              <div>
                <label className={labelClass}>Email</label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={editingUser.email || ""}
                    onChange={handleChange}
                    className={inputClass}
                  />
                ) : (
                  <p className={valueClass}>{user.email}</p>
                )}
              </div>

              <div>
                <label className={labelClass}>Role</label>
                {isEditing ? (
                  <select
                    name="role"
                    value={editingUser.role || "unassigned"}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="unassigned">Unassigned</option>
                    <option value="admin">Admin</option>
                    <option value="property_manager">Property Manager</option>
                  </select>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                    {formatRole(user.role)}
                  </span>
                )}
              </div>

              <div>
                <label className={labelClass}>Last Logged In</label>
                <p className={valueClass}>
                  {user.lastLogin
                    ? new Date(user.lastLogin).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "Never"}
                </p>
              </div>
            </div>
          </div>

          {/* Side card */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
            <h2 className="text-base font-semibold text-gray-900 mb-6">
              Account Status
            </h2>

            {/* Active toggle */}
            <div className="flex items-center justify-between py-4 border-b border-gray-100">
              <div>
                <p className="text-sm font-semibold text-gray-800">Active</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  User can log in and access the app
                </p>
              </div>
              {isEditing ? (
                <button
                  onClick={handleToggleActive}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${activeValue ? "bg-indigo-600" : "bg-gray-200"}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-300 ${activeValue ? "translate-x-5" : "translate-x-0"}`}
                  />
                </button>
              ) : (
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${user.active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${user.active ? "bg-green-500" : "bg-red-400"}`}
                  />
                  {user.active ? "Active" : "Inactive"}
                </span>
              )}
            </div>

            {/* Member since */}
            <div className="flex items-center justify-between py-4">
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  Member Since
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Account creation date
                </p>
              </div>
              <p className="text-sm font-medium text-gray-700">
                {user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
